
import React, { useState } from 'react';
import { useSupabaseAuth } from '@/hooks/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { RefreshCw, User, Shield, Database } from 'lucide-react';

const AdminAuthDebug: React.FC = () => {
  const { 
    session, 
    user, 
    profile, 
    userRole, 
    isLoading, 
    hasAccess, 
    isAdmin, 
    isStaff,
    refreshUserData 
  } = useSupabaseAuth();
  
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const runDiagnostics = async () => {
    if (!user) return;
    
    setIsRefreshing(true);
    try {
      console.log('=== RUNNING ADMIN AUTH DIAGNOSTICS ===');
      
      // Check role in database directly
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', user.id);
      
      // Check profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id);
      
      // Test RPC function
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_user_role_safe', {
        user_id_param: user.id
      });
      
      // Test admin functions
      const { data: adminTestData, error: adminTestError } = await supabase.rpc('is_admin_safe', {
        user_id_param: user.id
      });

      const diagnostics = {
        timestamp: new Date().toISOString(),
        user: {
          id: user.id,
          email: user.email,
          created_at: user.created_at
        },
        session: {
          exists: !!session,
          access_token_exists: !!session?.access_token,
          expires_at: session?.expires_at
        },
        profile: {
          data: profileData,
          error: profileError?.message
        },
        roles: {
          direct_query: roleData,
          direct_error: roleError?.message,
          rpc_result: rpcData,
          rpc_error: rpcError?.message,
          admin_test: adminTestData,
          admin_test_error: adminTestError?.message
        },
        frontend_state: {
          profile: profile,
          userRole: userRole,
          isAdmin: isAdmin(),
          isStaff: isStaff(),
          hasAdminAccess: hasAccess('admin'),
          hasStaffAccess: hasAccess('staff')
        }
      };
      
      console.log('Diagnostics:', diagnostics);
      setDebugInfo(diagnostics);
      
    } catch (error) {
      console.error('Error running diagnostics:', error);
      setDebugInfo({ error: error.message });
    } finally {
      setIsRefreshing(false);
    }
  };

  const forceRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshUserData();
      await runDiagnostics();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Admin Authentication Debug
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex gap-2">
          <Button onClick={runDiagnostics} disabled={!user || isRefreshing}>
            <Database className="h-4 w-4 mr-2" />
            Run Diagnostics
          </Button>
          <Button onClick={forceRefresh} disabled={!user || isRefreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Force Refresh
          </Button>
        </div>

        {/* Current State Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4" />
              <span className="font-medium">User</span>
            </div>
            <div className="text-sm">
              <div>Email: {user?.email || 'None'}</div>
              <div>ID: {user?.id ? `${user.id.slice(0, 8)}...` : 'None'}</div>
            </div>
          </div>
          
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4" />
              <span className="font-medium">Role</span>
            </div>
            <div className="text-sm">
              <Badge variant={userRole === 'admin' ? 'default' : 'secondary'}>
                {userRole || 'Unknown'}
              </Badge>
            </div>
          </div>
          
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Database className="h-4 w-4" />
              <span className="font-medium">Access</span>
            </div>
            <div className="text-sm space-y-1">
              <div>Admin: {isAdmin() ? '✅' : '❌'}</div>
              <div>Staff: {isStaff() ? '✅' : '❌'}</div>
            </div>
          </div>
          
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className="h-4 w-4" />
              <span className="font-medium">Status</span>
            </div>
            <div className="text-sm">
              <div>Loading: {isLoading ? '⏳' : '✅'}</div>
              <div>Approved: {profile?.is_approved ? '✅' : '❌'}</div>
            </div>
          </div>
        </div>

        {/* Detailed Debug Info */}
        {debugInfo && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3">Diagnostic Results</h3>
            <div className="bg-gray-50 p-4 rounded-lg overflow-auto">
              <pre className="text-xs">{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-medium mb-2">Quick Fixes for Admin Issues:</h3>
          <ul className="text-sm space-y-1">
            <li>1. Click "Force Refresh" to reload your role from the database</li>
            <li>2. If still having issues, sign out completely and sign back in</li>
            <li>3. Clear browser cache and localStorage if needed</li>
            <li>4. Check the diagnostic results for any database errors</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminAuthDebug;
